































































import Vue, { PropType } from "vue";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "@vue/composition-api";
import Chart from "chart.js/auto";
import { SIZE, TYPE, useGenAiPalette } from "../data/design";

export interface DonutItem {
  label: string;
  value: number;
  color: string;
}

/**
 * Chart view for a Gen AI card (gen-ai-ui WidgetDonutChart): a ring plus a
 * clickable legend of each slice's share. Slices with nothing in them are left
 * out of both, as the prototype does. Several render on one page, so the
 * canvas is reached through a template ref — the id-based lookup it was lifted
 * from assumed a single instance.
 */
export default Vue.extend({
  props: {
    items: { type: Array as PropType<DonutItem[]>, required: true },
    empty_text: { type: String, default: "No data for this view" },
  },
  setup(props, { emit }) {
    const palette = useGenAiPalette();
    // bound to `ref="canvas"`: the plugin fills it in on mount and on every
    // update, so it tracks the canvas appearing once there is data
    const canvas = ref<HTMLCanvasElement | null>(null);

    /** the slices actually drawn, each still pointing at its source row so a
     *  click opens the row the card would have opened */
    const shown = computed(() => {
      const items = props.items as DonutItem[];
      const total = items.reduce((sum, d) => sum + (d.value || 0), 0);
      return items
        .map((item, index) => ({
          item,
          index,
          label: item.label,
          value: item.value || 0,
          color: item.color,
          pct: total > 0 ? Math.round(((item.value || 0) / total) * 100) : 0,
        }))
        .filter((d) => d.value > 0);
    });

    let chart: Chart | null = null;

    const draw = () => {
      const el = canvas.value;
      chart?.destroy();
      chart = null;
      if (!el || !shown.value.length) return;
      const slices = shown.value;
      chart = new Chart(el, {
        type: "doughnut",
        // @ts-ignore — chart.js options typing lags the runtime API
        options: {
          cutout: "58%",
          layout: { padding: 2 },
          animation: { duration: 250 },
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              callbacks: {
                // "38% (1,786)", as the prototype's tooltip reads
                label: (ctx: { dataIndex: number }) => {
                  const d = slices[ctx.dataIndex];
                  return ` ${d.pct}% (${d.value.toLocaleString()})`;
                },
              },
            },
          },
          onClick: (_e: unknown, hits: { index: number }[]) => {
            if (hits && hits.length) {
              const d = slices[hits[0].index];
              emit("select", d.item, d.index);
            }
          },
        },
        data: {
          labels: slices.map((d) => d.label),
          datasets: [
            {
              data: slices.map((d) => d.value),
              backgroundColor: slices.map((d) => d.color),
              // the prototype's slices sit apart with no outline
              borderWidth: 0,
              spacing: 2,
              hoverOffset: 4,
            },
          ],
        },
      });
    };

    // the canvas only exists once there is data, so draw after the render that
    // adds it — a macrotask, which lands after the plugin has synced the ref
    const redraw = () => setTimeout(draw, 0);
    onMounted(redraw);
    watch(() => props.items, redraw, { deep: true, lazy: true });
    // canvas pixels cannot inherit CSS variables, so a theme flip repaints
    watch(palette, redraw, { lazy: true });
    onBeforeUnmount(() => chart?.destroy());

    return { canvas, shown, T: palette, TY: TYPE, S: SIZE };
  },
});
